import React from 'react';
import { action, observable, runInAction } from 'mobx';
import { Container, Inject } from 'typescript-ioc';
import { ICreateHubOrganizationDTO, IEventHubsDAO, IHubOrganizationDTO } from '../../../../api/event-hubs.dao';
import { EventHubsDAO } from '../../../../api/implementations/event-hubs.dao';
import { IAPIErrorObject, IErrorParsing } from '../../../../infrastructure/error-parsing';
import { showNotification } from '../../../global/modal/alerts-host/alert-templates';
import TrackingServiceProvider from '../../../../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS } from '../../../../tracking-service/tracking-values';
import {
    DELETE_HUB_ORGANIZATION_SUCCESS_TOAST_TEXT,
    EDIT_HUB_ORGANIZATION_UPDATED_NOTIFICATION_TEXT,
    HUB_ORGANIZATION_DELETION_FAILED_POPUP_TEXT,
    HUB_ORGANIZATION_DELETION_STARTED_TEXT,
    HUB_ORGANIZATION_DELETION_SUCCESS_POPUP_TEXT,
    NEW_ORGANIZATION_ADDED_NOTIFICATION_TEXT
} from '../../event-hubs.text';
import { IAccountsDAO } from '../../../../api/accounts.dao';
import { AccountsDAO } from '../../../../api/implementations/accounts.dao';
import { HubOrganizationFormDataViewModel } from './hub-organization-form-data.viewmodel';
import { IAlertsService } from '../../../../infrastructure/alerts';
import { SortOrder } from '../../../../api/shared.dto';
import { IJobDescriptionDTO, JobDetailsTypes, JobResultTypes, JobStatus, JobType } from '../../../../api/jobs.dao';
import { IJobsMonitor } from '../../../../domain/jobs-monitor/jobs-monitor';

export class HubOrganizationFormVM {
    @observable
    public isSaving: boolean = false;
    @observable
    private organizationFormDataVM: HubOrganizationFormDataViewModel;

    constructor(
        private readonly accountId: number,
        private readonly eventHubId: number,
        private readonly updateOrganizationOnList: (organization?: IHubOrganizationDTO) => void,
        private readonly closeHubOrganizationPopup: () => void = () => undefined,
        private readonly initialHubOrganization: IHubOrganizationDTO | undefined = undefined,
        @Inject private readonly eventHubsDAO: IEventHubsDAO = Container.get(EventHubsDAO),
        @Inject private readonly errorParsingService: IErrorParsing = Container.get(IErrorParsing),
        @Inject private readonly trackingService: TrackingServiceProvider = Container.get(TrackingServiceProvider),
        @Inject private readonly accountsDAO: IAccountsDAO = Container.get(AccountsDAO),
        @Inject private readonly alertsService: IAlertsService = Container.get(IAlertsService),
        @Inject private readonly jobsMonitor: IJobsMonitor = Container.get(IJobsMonitor),
    ) {
        this.isSaving = false;
    }

    public formSubmitAction = async (): Promise<boolean> => {
        if (!this.organizationFormDataVM.checkForm()) {
            return false;
        }

        return await this.updateOrganizationCall();
    };

    public getOrganizationFormDataVM() {
        return this.organizationFormDataVM;
    }

    public getIsEditMode(): boolean {
        return this.initialHubOrganization != null;
    }

    public deleteOrganization = (): void => {
        void this.confirmDeleteOrganization();
    };

    @action
    public async initialize() {
        if (this.initialHubOrganization) {
            this.organizationFormDataVM = new HubOrganizationFormDataViewModel({
                name: this.initialHubOrganization.name ?? '',
                organizationTicker: this.initialHubOrganization.ticker ?? '',
                organizationType: this.initialHubOrganization.type ?? '',
                externalOrganizationId: this.initialHubOrganization.externalOrganizationId ?? '',
            });
        } else {
            this.organizationFormDataVM = new HubOrganizationFormDataViewModel({
                name: '',
                organizationTicker: '',
                organizationType: '',
                externalOrganizationId: '',
            });
        }
    }

    @action
    public handleApiException(apiError: IAPIErrorObject): void {
        const apiToFormFieldMap: { [key: string]: string } = {
            name: 'name',
            type: 'organizationType',
            ticker: 'organizationTicker',
            externalOrganizationId: 'externalOrganizationId',
        };
        const apiFieldNames = Object.keys(apiToFormFieldMap);

        if (apiFieldNames.length && apiError?.fields?.length) {
            const fieldsMatch = HubOrganizationFormVM.getFieldsMatchedByList(apiFieldNames, apiError.fields);

            if (Reflect.ownKeys(fieldsMatch).length) {
                this.organizationFormDataVM.setServerErrorMessage('');
                Object.entries(fieldsMatch).forEach(([apiField, message]) => {
                    const formField = apiToFormFieldMap[apiField];
                    this.organizationFormDataVM.setFieldError(formField, message);
                });
                return;
            }
        }

        this.organizationFormDataVM.clearAllFieldErrors();
        this.organizationFormDataVM.setServerErrorMessage(apiError.errorMessage || 'An error occurred');
    }

    private static getFieldsMatchedByList(fieldNames: string[], fields: IAPIErrorObject['fields']): { [key: string]: string } {
        return fields.reduce((match, { name, message }) => {
            if (fieldNames.includes(name)) match[name] = message;
            return match;
        }, {});
    }

    private async confirmDeleteOrganization(): Promise<void> {
        if (!this.initialHubOrganization) {
            return;
        }

        const { totalCount, attendeeNamesPreview } = await this.getOrganizationAttendeesPreview(this.initialHubOrganization.hubOrganizationId);

        this.alertsService.confirm({
            message: this.buildDeleteConfirmationMessage(attendeeNamesPreview, totalCount),
            confirmButtonProps: {
                text: 'Yes, Delete',
                modifier: 'confirm-warn',
                handler: () => {
                    void this.deleteHubOrganizationApiCall(totalCount);
                },
            },
            cancelButtonProps: {
                modifier: 'cancel',
            },
        });
    }

    private buildDeleteConfirmationMessage(attendeeNamesPreview: string[], totalCount: number): React.ReactNode {
        const baseMessage = React.createElement(
            React.Fragment,
            null,
            React.createElement('span', { className: 'alert-message' }, 'Are you sure you want to '),
            React.createElement('span', { className: 'alert-message--danger' }, 'delete this organization'),
            React.createElement('span', { className: 'alert-message' }, '?')
        );

        if (!totalCount) {
            return baseMessage;
        }

        const extraCount = Math.max(totalCount - attendeeNamesPreview.length, 0);
        const suffix = extraCount ? `+ ${extraCount} more` : '';

        return React.createElement(
            React.Fragment,
            null,
            baseMessage,
            React.createElement(
                'span',
                { className: 'alert-message' },
                ' The following attendees will also be deleted and will no longer be able to access the Hub: '
            ),
            React.createElement(
                'div',
                {
                    style: {
                        marginTop: '8px',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                    }
                },
                React.createElement(
                    'div',
                    { style: { width: '320px', textAlign: 'center' } },
                    attendeeNamesPreview.map((name, index) => {
                        return React.createElement(
                            'div',
                            {
                                key: `${name}-${index}`,
                                style: {
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                },
                            },
                            name,
                        );
                    }),
                    extraCount > 0
                        ? React.createElement(
                            'div',
                            {
                                style: {
                                    display: 'flex',
                                    justifyContent: 'center',
                                    marginTop: '4px',
                                    whiteSpace: 'nowrap',
                                }
                            },
                            React.createElement('span', null, suffix)
                        )
                        : null
                )
            ),
        );
    }

    private async getOrganizationAttendeesPreview(organizationId: number): Promise<{ attendeeNamesPreview: string[]; totalCount: number }> {
        const pageSize = 200;
        let page = 0;
        let totalElements = 0;
        let linkedAttendeeNames: string[] = [];

        try {
            do {
                const response = await this.eventHubsDAO.getHubAttendees({
                    accountId: this.accountId,
                    eventHubId: this.eventHubId,
                    page: page,
                    size: pageSize,
                    sortField: 'lastName',
                    sortOrder: SortOrder.Asc,
                });

                totalElements = response.totalElements;
                linkedAttendeeNames = linkedAttendeeNames.concat(
                    response.content
                        .filter(attendee => attendee.organizationId === organizationId)
                        .map(attendee => [attendee.firstName, attendee.lastName].filter(Boolean).join(' ').trim())
                        .filter(Boolean),
                );
                page += 1;
            } while (page * pageSize < totalElements);
        } catch {
            return {
                attendeeNamesPreview: [],
                totalCount: 0,
            };
        }

        return {
            attendeeNamesPreview: linkedAttendeeNames.slice(0, 10),
            totalCount: linkedAttendeeNames.length,
        };
    }

    private waitForDeleteJobCompletion(jobId: number | string, jobType: JobType, timeoutMs: number = 120000): Promise<JobStatus | null> {
        return new Promise(resolve => {
            let completed = false;
            let timeoutId: number | undefined;

            const complete = (status: JobStatus | null) => {
                if (completed) {
                    return;
                }
                completed = true;
                if (timeoutId != null) {
                    window.clearTimeout(timeoutId);
                }
                this.jobsMonitor.removeHandler(jobType, onDeleteOrganizationJobUpdate);
                resolve(status);
            };

            const onDeleteOrganizationJobUpdate = (job: IJobDescriptionDTO<JobDetailsTypes, JobResultTypes | null>) => {
                if (String(job.jobId) !== String(jobId)) {
                    return;
                }
                if (job.jobStatus === JobStatus.Completed || job.jobStatus === JobStatus.Failed) {
                    complete(job.jobStatus);
                }
            };

            this.jobsMonitor.registerHandler(jobType, onDeleteOrganizationJobUpdate);

            if (this.jobsMonitor.cacheExists()) {
                const cachedJob = this.jobsMonitor
                    .getJobsByTypeFromCache(jobType)
                    .find(job => String(job.jobId) === String(jobId));

                if (cachedJob && (cachedJob.jobStatus === JobStatus.Completed || cachedJob.jobStatus === JobStatus.Failed)) {
                    complete(cachedJob.jobStatus);
                    return;
                }
            }

            timeoutId = window.setTimeout(() => {
                complete(null);
            }, timeoutMs);
        });
    }

    @action
    private async deleteHubOrganizationApiCall(attendeeCount: number): Promise<void> {
        if (!this.initialHubOrganization) {
            return;
        }

        this.isSaving = true;
        showNotification(HUB_ORGANIZATION_DELETION_STARTED_TEXT.defaultMessage);
        this.closeHubOrganizationPopup();

        try {
            const triggeredJob = await this.eventHubsDAO.deleteHubOrganization(
                this.accountId,
                this.eventHubId,
                this.initialHubOrganization.hubOrganizationId,
            );

            const hasJobId = !!triggeredJob && typeof triggeredJob === 'object' && 'jobId' in triggeredJob && triggeredJob.jobId != null;
            const hasJobType = !!triggeredJob && typeof triggeredJob === 'object' && 'jobType' in triggeredJob && triggeredJob.jobType != null;
            const finalStatus = hasJobId
                ? await this.waitForDeleteJobCompletion(triggeredJob.jobId, hasJobType ? triggeredJob.jobType : JobType.DeleteOrganization)
                : JobStatus.Completed;

            if (finalStatus !== JobStatus.Completed) {
                this.alertsService.alert({
                    message: HUB_ORGANIZATION_DELETION_FAILED_POPUP_TEXT.defaultMessage,
                    okButtonProps: {
                        text: 'Ok',
                    },
                });
                return;
            }

            try {
                await this.trackDeleteHubOrganization(attendeeCount);
            } catch {
                // tracking failure should not block deletion success flow
            }

            this.alertsService.alert({
                message: HUB_ORGANIZATION_DELETION_SUCCESS_POPUP_TEXT.defaultMessage,
                okButtonProps: {
                    text: 'Ok',
                    handler: () => {
                        this.updateOrganizationOnList();
                        showNotification(
                            DELETE_HUB_ORGANIZATION_SUCCESS_TOAST_TEXT.defaultMessage.replace(
                                '{organizationName}',
                                this.initialHubOrganization.name,
                            ),
                        );
                    },
                },
            });
        } catch {
            this.alertsService.alert({
                message: HUB_ORGANIZATION_DELETION_FAILED_POPUP_TEXT.defaultMessage,
                okButtonProps: {
                    text: 'Ok',
                },
            });
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    private async trackDeleteHubOrganization(attendeeCount: number): Promise<void> {
        const accountDetails = await this.accountsDAO.readAccount(this.accountId);
        this.trackingService.get().track(TRACKING_EVENTS.DELETE_HUB_ORGANIZATION, {
            'Account Name': accountDetails.name,
            'Account ID': this.accountId,
            'Hub ID': this.eventHubId,
            'Organization ID': this.initialHubOrganization?.hubOrganizationId,
            'Had Associated Attendees': attendeeCount > 0,
            'Attendee Count': attendeeCount,
        });
    }

    @action
    private async updateOrganizationCall() {
        this.isSaving = true;
        try {
            const dto: ICreateHubOrganizationDTO = {
                name: this.organizationFormDataVM.name.trim(),
                type: this.organizationFormDataVM.organizationType.trim() || undefined,
                ticker: this.organizationFormDataVM.organizationTicker.trim() || undefined,
                externalOrganizationId: this.organizationFormDataVM.externalOrganizationId.trim() || undefined,
            };

            const accountDetails = await this.accountsDAO.readAccount(this.accountId);

            if (this.initialHubOrganization) {
                const updatedOrganization = await this.eventHubsDAO.updateHubOrganization(
                    this.accountId,
                    this.eventHubId,
                    this.initialHubOrganization.hubOrganizationId,
                    dto
                );
                this.trackingService.get().track(TRACKING_EVENTS.EDIT_HUB_ORGANIZATION, {
                    'Account Name': accountDetails.name,
                    'Account ID': this.accountId,
                    'Hub ID': this.eventHubId,
                    'Organization ID': this.initialHubOrganization.hubOrganizationId,
                });
                showNotification(EDIT_HUB_ORGANIZATION_UPDATED_NOTIFICATION_TEXT.defaultMessage);
                this.updateOrganizationOnList(updatedOrganization);
            } else {
                const createdOrganization = await this.eventHubsDAO.createHubOrganization(this.accountId, this.eventHubId, dto);
                this.trackingService.get().track(TRACKING_EVENTS.CREATE_HUB_ORGANIZATION, {
                    'Account Name': accountDetails.name,
                    'Account ID': this.accountId,
                    'Hub ID': this.eventHubId,
                });
                showNotification(NEW_ORGANIZATION_ADDED_NOTIFICATION_TEXT.defaultMessage);
                this.updateOrganizationOnList(createdOrganization);
            }
            return true;
        } catch (e) {
            if (e.response?.status === 403) {
                const apiError = this.errorParsingService.extractAPIError(e);
                this.handleApiException(apiError);
            } else {
                const apiError = this.errorParsingService.extractAPIError(e);
                this.handleApiException(apiError);
            }
            return false;
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }
}

